/**
 * cryptoUtils.js
 */

const PASSPHRASE = "CHANGE_ME_IN_ENV"; // ← set VITE_STORAGE_SECRET in your .env

// How many PBKDF2 iterations to use (higher = slower brute-force, slower UI).
// 100_000 is the OWASP-recommended minimum as of 2023.
const PBKDF2_ITERATIONS = 100_000;

// ─── LOW-LEVEL HELPERS ────────────────────────────────────────────────────────

/** Encode a UTF-8 string to a Uint8Array */
const encode = (str) => new TextEncoder().encode(str);

/** Decode a Uint8Array back to a UTF-8 string */
const decode = (buf) => new TextDecoder().decode(buf);

/** Convert a Uint8Array to a base64 string (safe for localStorage) */
const toBase64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

/** Convert a base64 string back to a Uint8Array */
const fromBase64 = (b64) =>
  Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));

// ─── KEY DERIVATION ───────────────────────────────────────────────────────────

/**
 * Derives a 256-bit AES-GCM CryptoKey from the passphrase + salt using PBKDF2.
 * @param {Uint8Array} salt - Random 16-byte salt
 * @returns {Promise<CryptoKey>}
 */
const deriveKey = async (salt) => {
  // Import the passphrase as raw key material
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    encode(PASSPHRASE),
    { name: "PBKDF2" },
    false, // not extractable
    ["deriveKey"]
  );

  // Derive a 256-bit AES-GCM key
  return crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: PBKDF2_ITERATIONS,
      hash: "SHA-256",
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false, // not extractable
    ["encrypt", "decrypt"]
  );
};

// ─── PUBLIC API ───────────────────────────────────────────────────────────────

/**
 * Encrypts a value and stores it in localStorage under the given key.
 *
 * Storage format (all base64, pipe-separated):
 *   "<salt_b64>|<iv_b64>|<ciphertext_b64>"
 *
 * @param {string} storageKey   - The localStorage key (e.g. "token")
 * @param {unknown} value       - Any JSON-serialisable value
 */
export const setEncrypted = async (storageKey, value) => {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    console.warn(`[cryptoUtils] Value for "${storageKey}" is not JSON-serialisable (undefined). Skipping.`);
    return;
  }
  try {
    const salt = crypto.getRandomValues(new Uint8Array(16)); // 128-bit salt
    const iv   = crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV (AES-GCM standard)

    const key = await deriveKey(salt);

    const plaintext  = encode(serialized);
    const ciphertext = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv },
      key,
      plaintext
    );

    const payload = [toBase64(salt), toBase64(iv), toBase64(ciphertext)].join("|");
    localStorage.setItem(storageKey, payload);
  } catch (err) {
    console.error(`[cryptoUtils] setEncrypted failed for key "${storageKey}":`, err);
  }
};

/**
 * Reads and decrypts a value from localStorage.
 *
 * @param {string} storageKey - The localStorage key
 * @returns {Promise<unknown|null>} - The original value, or null if missing / tampered
 */
export const getDecrypted = async (storageKey) => {
  const payload = localStorage.getItem(storageKey);
  if (!payload) return null;

  const parts = payload.split("|");
  if (parts.length !== 3) {
    console.warn(`[cryptoUtils] "${storageKey}" is not encrypted — returning raw value.`);
    return payload;
  }

  const [saltB64, ivB64, ciphertextB64] = parts;
  const salt       = fromBase64(saltB64);
  const iv         = fromBase64(ivB64);
  const ciphertext = fromBase64(ciphertextB64);

  let plaintext;
  try {
    const key = await deriveKey(salt);
    plaintext = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertext);
  } catch (err) {
    // AES-GCM auth tag mismatch → genuinely tampered or wrong key
    console.error(`[cryptoUtils] Decryption failed for "${storageKey}" — removing entry.`, err);
    localStorage.removeItem(storageKey);
    return null;
  }

  // Decryption succeeded — parse separately so a bad value doesn't nuke the entry
  try {
    return JSON.parse(decode(plaintext));
  } catch (err) {
    console.error(`[cryptoUtils] JSON.parse failed for "${storageKey}" — decrypted bytes were malformed. Value stored:`, decode(plaintext));
    // Do NOT remove the entry here; log and return null so you can investigate
    return null;
  }
};

/**
 * Convenience wrapper: encrypts & stores multiple key-value pairs at once.
 * @param {Record<string, unknown>} entries
 */
export const setEncryptedBatch = async (entries) => {
  await Promise.all(
    Object.entries(entries).map(([k, v]) => setEncrypted(k, v))
  );
};

/**
 * Convenience wrapper: reads & decrypts multiple keys at once.
 * @param {string[]} keys
 * @returns {Promise<Record<string, unknown>>}
 */
export const getDecryptedBatch = async (keys) => {
  const values = await Promise.all(keys.map((k) => getDecrypted(k)));
  return Object.fromEntries(keys.map((k, i) => [k, values[i]]));
};